import { css } from 'aphrodite';
import * as React from 'react';
import { ISupportGitHubIssueLink, ISupportGitHubTriageRun } from '../../../models/Support/ISupportGitHub';
import { useSupportGitHubIssueRunsQuery, useSyncGitHubIssueMutation } from '../../../queries/Support/useSupportGitHub';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { Button } from '../../../web/components-v2/Button';
import { GitHubEscalationConfirmModal } from './GitHubIssueModals';
import {
	canEscalateIssue,
	ConfidenceMeter,
	formatConfidence,
	getConfidenceMeter,
	getInvestigationLifecycle,
	getStatusPill,
	GitHubPanelTone,
	hasInvestigationResult,
} from './githubPanelPresentation';
import { GitHubIssueMetaRow, GitHubRecordHeader, PILL_TONE_STYLES } from './GitHubRecordCard';
import { styles } from './styles';
import { formatShortDateTime } from './supportTicketPresentation';

const METER_FILL_STYLES: Partial<Record<GitHubPanelTone, (typeof styles)[keyof typeof styles]>> = {
	positive: styles.githubMeterFillPositive,
	caution: styles.githubMeterFillCaution,
	critical: styles.githubMeterFillCritical,
};

/**
 * The managed-investigation record: lifecycle position first, then the result the workflow reported, then
 * the actions available at that point in the lifecycle.
 */
export const GitHubInvestigationCard: React.FC<{
	ticketId: string;
	issue: ISupportGitHubIssueLink;
	enabled: boolean;
}> = ({ ticketId, issue, enabled }) => {
	const [showEscalateConfirm, setShowEscalateConfirm] = React.useState(false);
	const [showRuns, setShowRuns] = React.useState(false);
	const syncMutation = useSyncGitHubIssueMutation(ticketId, issue.id);
	const runsQuery = useSupportGitHubIssueRunsQuery(ticketId, issue.id, showRuns);
	const lifecycle = getInvestigationLifecycle(issue);
	const statusPill = getStatusPill(issue);

	return (
		<div className={css(styles.githubRecordCard, styles.githubRecordCardInvestigation)}>
			<GitHubRecordHeader issue={issue} />

			<div className={css(styles.githubLifecycle)}>
				<div className={css(styles.githubLifecycleTrack)}>
					{Array.from({ length: lifecycle.total }, (_unused, index) => (
						<div
							key={index}
							className={css(
								styles.githubLifecycleSegment,
								index < lifecycle.stage && styles.githubLifecycleSegmentReached,
								index === lifecycle.stage - 1 && lifecycle.inFlight && styles.githubLifecycleSegmentInFlight
							)}
						/>
					))}
				</div>
				<div className={css(styles.githubLifecycleRow)}>
					<span className={css(styles.githubStatusPill, PILL_TONE_STYLES[statusPill.tone])}>{statusPill.label}</span>
					<span className={css(styles.githubStageText)}>
						Step {lifecycle.stage} of {lifecycle.total} · {lifecycle.stageLabel}
					</span>
				</div>
			</div>

			{hasInvestigationResult(issue) ? (
				<div className={css(styles.githubResult)}>
					<ConfidenceRow issue={issue} />
					{issue.latestSummary && <div className={css(styles.githubResultSummary)}>{issue.latestSummary}</div>}
					<Button
						variant='link'
						size='sm'
						styles={[styles.githubRunsToggle]}
						onClick={() => setShowRuns(open => !open)}
					>
						{showRuns ? 'Hide investigation history' : 'View investigation history'} ·{' '}
						{formatShortDateTime(issue.latestResultAt)}
					</Button>
					{showRuns && <RunHistory query={runsQuery} latestTriageRunId={issue.latestTriageRunId} />}
				</div>
			) : (
				<div className={css(styles.githubStageText)}>
					Results land here once the repository workflow reports back. Use Refresh to check for an update.
				</div>
			)}

			<GitHubIssueMetaRow issue={issue} />

			<div className={css(styles.githubCardFooter)}>
				{canEscalateIssue(issue) && (
					<Button variant='primary' size='sm' onClick={() => setShowEscalateConfirm(true)}>
						Escalate to Engineering
					</Button>
				)}
				<Button
					variant='outline'
					size='sm'
					onClick={() => syncMutation.mutate()}
					disabled={!enabled || syncMutation.isPending}
				>
					{syncMutation.isPending ? 'Refreshing...' : 'Refresh'}
				</Button>
				<span className={css(styles.githubCardFooterNote)}>Managed · can't unlink</span>
			</div>

			{syncMutation.isError && (
				<div className={css(styles.githubPanelError)} role='alert' aria-live='polite'>
					{getApiErrorMessage(syncMutation.error, 'Failed to refresh GitHub issue.')}
				</div>
			)}
			{showEscalateConfirm && (
				<GitHubEscalationConfirmModal ticketId={ticketId} issue={issue} onClose={() => setShowEscalateConfirm(false)} />
			)}
		</div>
	);
};

/**
 * The API reports confidence on a three-value scale, so the meter has three steps. A value outside
 * that scale is shown verbatim rather than mapped onto a step it did not claim.
 */
const ConfidenceRow: React.FC<{ issue: ISupportGitHubIssueLink }> = ({ issue }) => {
	// The API already sends the disposition in prose casing, so it needs no mapping — only a
	// stand-in for a result that landed without one.
	const dispositionLabel = issue.latestDisposition ?? 'Result received';
	const meter = getConfidenceMeter(issue.latestConfidence);

	if (!meter) {
		return (
			<div className={css(styles.githubMeterRow)}>
				<span className={css(styles.githubMeterDisposition)}>{dispositionLabel}</span>
				{issue.latestConfidence && <span>{formatConfidence(issue.latestConfidence)} confidence</span>}
			</div>
		);
	}

	return (
		<div className={css(styles.githubMeter)}>
			<div className={css(styles.githubMeterRow)}>
				<span className={css(styles.githubMeterDisposition)}>{dispositionLabel}</span>
				<span>{meter.label}</span>
			</div>
			<div className={css(styles.githubMeterTrack)}>
				<MeterFill meter={meter} />
			</div>
		</div>
	);
};

// Aphrodite has no way to express a data-driven width, so the fill keeps an inline width.
const MeterFill: React.FC<{ meter: ConfidenceMeter }> = ({ meter }) => (
	<div
		className={css(styles.githubMeterFill, METER_FILL_STYLES[meter.tone])}
		style={{ width: `${meter.fillPercent}%` }}
	/>
);

const RunHistory: React.FC<{
	query: ReturnType<typeof useSupportGitHubIssueRunsQuery>;
	latestTriageRunId?: string;
}> = ({ query, latestTriageRunId }) => {
	if (query.isLoading) {
		return <div className={css(styles.githubRunsPanel, styles.githubRunMeta)}>Loading investigation history...</div>;
	}

	if (query.isError) {
		return (
			<div className={css(styles.githubRunsPanel)} role='alert' aria-live='polite'>
				<span className={css(styles.githubRunMeta)}>
					{getApiErrorMessage(query.error, 'Failed to load the investigation history.')}
				</span>
			</div>
		);
	}

	const runs = query.data ?? [];
	if (!runs.length) {
		return <div className={css(styles.githubRunsPanel, styles.githubRunMeta)}>No completed runs recorded yet.</div>;
	}

	return (
		<div className={css(styles.githubRunsPanel)}>
			{runs.map((run, index) => (
				<RunRow key={run.id} run={run} label={`Run ${runs.length - index}`} isLatest={run.id === latestTriageRunId} />
			))}
			<span className={css(styles.githubRunMeta)}>
				{runs.length} {runs.length === 1 ? 'run' : 'runs'} · latest attempt {runs[0].workflowRunAttempt}
			</span>
		</div>
	);
};

const RunRow: React.FC<{ run: ISupportGitHubTriageRun; label: string; isLatest: boolean }> = ({
	run,
	label,
	isLatest,
}) => (
	<div className={css(styles.githubRunRow, isLatest && styles.githubRunRowLatest)}>
		<span>
			{label} · {formatShortDateTime(run.receivedAt)}
			{isLatest ? ' · latest' : ''}
		</span>
		<span>
			{run.disposition}
			{run.confidence ? ` · ${formatConfidence(run.confidence)}` : ''}
		</span>
	</div>
);
